/**
 * Ansichts-Wache und Menü nach Rechten (docs/rollensystem-plan.md §6.1, §11 C1).
 *
 * EINE Regel für drei Stellen: `App.tsx` rendert ohne Recht „Kein Zugriff“
 * statt der Ansicht, `Sidebar.tsx` und `MobileNavigation.tsx` zeigen den
 * Eintrag gar nicht erst. Stünde die Regel an jeder Stelle einzeln, liefen
 * Menü und Wache beim nächsten Eintrag auseinander — ein Menüpunkt, der auf
 * „Kein Zugriff“ führt, oder eine Ansicht, die nur über einen alten Link
 * erreichbar ist.
 *
 * Nur die Ansichten, die einem Recht entsprechen, stehen in der Tabelle; alle
 * anderen bleiben frei wie heute. Admin und Super-Admin sehen alles. Ohne
 * Migration gelten die Rechte der Systemrolle (`rechteAusSystemrolle`) — damit
 * sieht `user`/`arbeitsvorbereitung` genau das Menü von heute.
 *
 * Rein: kein Supabase, kein React. Die Rechte kommen als `RechteSicht` (nur
 * der Typ wird importiert), Tests bauen sie aus einer Menge.
 */
import type { AppView } from '../../types';
import { anlageTypVon, anlegeRecht } from '../projektAnlageRechte';
import { RECHTE, type RechtSchluessel } from './katalog';
import type { RechteSicht } from './rechteService';

/** Was die Wache von den Rechten braucht. */
export type AnsichtRechteSicht = Pick<RechteSicht, 'hat'>;

export interface AnsichtExtras {
    /**
     * Häkchen „SiFa“ (Nr. 141, `istSifaFachkraft`): gibt die Unterweisungs-
     * Vorlagen frei wie das Recht `sifa.vorlagen` — ODER, nicht statt.
     */
    istSifaFachkraft: boolean;
}

/**
 * Ansicht → Rechte, von denen EINES genügt. Was hier fehlt, ist frei.
 * `crm` ist die Kundenakte: Vertrieb mit nur „Angebote“ braucht sie, um ein
 * Angebot einem Kunden zuzuordnen. `invoices` öffnet jedes Rechnungsrecht —
 * dieselbe Vereinigung, mit der die Datenbank (Nr. 151) das Lesen erlaubt.
 * `material` öffnet jedes der beiden Material-Rechte — dieselbe Vereinigung
 * wie die Lesepolicy der Material-Tabellen (Nr. 178). `talentpool` hängt am
 * einen Recht, das auch die Lesepolicy der Tabelle verlangt (Nr. 188).
 */
const ANSICHT_RECHTE: Readonly<Partial<Record<AppView, readonly RechtSchluessel[]>>> = {
    invoices: ['rechnungen.lesen', 'rechnungen.bearbeiten', 'rechnungen.ausstellen', 'zahlungen.buchen', 'datev.export'],
    crm: ['kunden.verwalten', 'angebote.verwalten'],
    leads: ['leads.verwalten'],
    'crm-map': ['leads.verwalten'],
    quotes: ['angebote.verwalten'],
    freelancers: ['freelancer.verwalten'],
    'erp-cockpit': ['erp.steuerung'],
    'sifa-vorlagen': ['sifa.vorlagen'],
    material: ['material.beschaffen', 'material.verwalten'],
    talentpool: ['talentpool.verwalten'],
};

const istAdmin = (systemrolle: string | null | undefined): boolean =>
    systemrolle === 'admin' || systemrolle === 'super_admin';

/** Die Rechte, von denen eines die Ansicht freigibt; leer = frei für alle. */
export const ansichtBraucht = (view: AppView): readonly RechtSchluessel[] =>
    ANSICHT_RECHTE[view] ?? [];

/**
 * Darf die angemeldete Person diese Ansicht sehen?
 *
 * Freie Ansichten: immer (wie heute — welche davon jemand wirklich nutzen
 * kann, entscheiden Datenbank und Ansicht selbst). Admin und Super-Admin:
 * immer. Freelancer: keine der Ansichten mit Recht, auch nicht über das
 * Häkchen — Rechte gelten nur für interne Nutzer.
 */
export const ansichtErlaubt = (
    view: AppView,
    rechte: AnsichtRechteSicht,
    systemrolle: string | null | undefined,
    extras: AnsichtExtras,
): boolean => {
    const noetig = ANSICHT_RECHTE[view];
    if (!noetig) return true;
    if (istAdmin(systemrolle)) return true;
    if (systemrolle === 'freelancer') return false;
    if (view === 'sifa-vorlagen' && extras.istSifaFachkraft) return true;
    return noetig.some(r => rechte.hat(r));
};

/** Was App.tsx an der Stelle der Ansicht rendert. */
export type AnsichtWache = 'frei' | 'laedt' | 'gesperrt';

/**
 * Die Wache in App.tsx: frei, gesperrt — oder noch nicht entscheidbar.
 *
 * „Kein Zugriff“ erst, wenn feststeht, dass das Recht fehlt. Solange das
 * Profil (Systemrolle), die Rechte aus der Datenbank oder — für die
 * SiFa-Vorlagen — das Häkchen noch laden, stünde die Karte nach F5 sonst
 * für eine Sekunde da, bis die Rechte da sind; das sähe aus wie ein
 * entzogenes Recht.
 */
export const ansichtWache = (
    view: AppView,
    rechte: Pick<RechteSicht, 'hat' | 'geladen'>,
    systemrolle: string | null | undefined,
    extras: AnsichtExtras & { sifaGeladen?: boolean },
): AnsichtWache => {
    if (ansichtErlaubt(view, rechte, systemrolle, extras)) return 'frei';
    if (!systemrolle || !rechte.geladen) return 'laedt';
    if (view === 'sifa-vorlagen' && extras.sifaGeladen === false) return 'laedt';
    return 'gesperrt';
};

const rechtTitel = (recht: RechtSchluessel): string =>
    RECHTE.find(r => r.schluessel === recht)?.titel ?? recht;

/**
 * Wo ein Admin das Recht vergibt — der Menüpfad, wie er in der Seitenleiste
 * steht (Menü-Umbau 19.10.2026: „Rollen & Rechte“ ist ein Reiter im Bereich
 * „Benutzer & Rechte“ unter „Verwaltung“, adminBereiche.ts). Einmal hier,
 * damit beide Sätze beim nächsten Umbau nicht auseinanderlaufen.
 */
const WEG_ZU_DEN_RECHTEN =
    'Ein Admin kann es unter Verwaltung → Benutzer & Rechte → Rollen & Rechte vergeben.';

/**
 * Satz für die Karte „Kein Zugriff“. Mehrere Rechte („Kunden“ ODER
 * „Angebote“) werden mit „oder“ genannt — eines davon reicht.
 */
export const keinZugriffText = (view: AppView): string => {
    const noetig = ansichtBraucht(view);
    // Mehr als zwei Rechte (Rechnungen: jedes Rechnungsrecht öffnet) würden
    // zur Aufzählung — dann das erste nennen, das die meisten brauchen.
    const was = noetig.length === 0
        ? 'ein Recht'
        : noetig.length <= 2
            ? `das Recht ${noetig.map(r => `„${rechtTitel(r)}“`).join(' oder ')}`
            : `das Recht „${rechtTitel(noetig[0])}“ (oder ein anderes Recht dieses Bereichs)`;
    return `Für diesen Bereich fehlt Ihnen ${was}. `
        + WEG_ZU_DEN_RECHTEN;
};

// ─── Projekt anlegen ─────────────────────────────────────────────────────────

/**
 * Klartext, wenn dieser Projekttyp (`metadata.projectType`, fehlend = SiGeKo)
 * nicht angelegt werden darf — sonst null. Für Import und Assistent, bevor
 * gespeichert wird: die Datenbank wiese die Anlage ab Nr. 154 ohnehin mit
 * 42501 ab, aber erst nach dem Hochladen. Typ → Recht steht an EINER Stelle
 * (`services/projektAnlageRechte.ts`, auch für den Assistenten).
 */
export const projektAnlegenVerweigert = (
    rechte: AnsichtRechteSicht,
    projectType: string | null | undefined,
): string | null => {
    const recht = anlegeRecht(anlageTypVon(projectType));
    if (rechte.hat(recht)) return null;
    return `Dieses Projekt kann nicht angelegt werden: Ihnen fehlt das Recht „${rechtTitel(recht)}“. `
        + WEG_ZU_DEN_RECHTEN;
};
